import React, { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Factory, GraduationCap, HeartPulse, Store } from "lucide-react";

const industries = [
  {
    icon: Building2,
    name: "Corporate Offices",
    short: "Snacks and beverage vending for lounges, cafés…",
    desc: "Snacks and beverage vending for lounges, cafés and meeting floors. Branded, cashless first.",
    image:
      "https://images.unsplash.com/photo-1759301495186-d3787210df5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBicmVhayUyMHJvb218ZW58MHx8fHwxNzgyMzgyMzEwfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Factory,
    name: "Manufacturing & Factories",
    short: "Dedicated PPE vending",
    desc: "Dedicated PPE vending. Auditable issuance, lower shrinkage, safer floors.",
    image:
      "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjB3b3JrZXIlMjBzYWZldHklMjBQUEV8ZW58MHx8fHwxNzgyMzgyMzEwfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: GraduationCap,
    name: "Educational Institutions",
    short: "Stationery, snacks and hygiene SKUs",
    desc: "Stationery, snacks and hygiene SKUs for hostels, labs and student hubs.",
    image:
      "https://images.pexels.com/photos/32255385/pexels-photo-32255385.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: HeartPulse,
    name: "Hospitals & Healthcare",
    short: "24×7 PPE, hygiene and beverage vending",
    desc: "24×7 PPE, hygiene and beverage vending you own — validated and reliable.",
    image:
      "https://images.unsplash.com/photo-1768659347688-b535745d0ff3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBicmVhayUyMHJvb218ZW58MHx8fHwxNzgyMzgyMzEwfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Store,
    name: "Retail & Public Spaces",
    short: "Rugged machines, universal payments and live monitoring",
    desc: "Rugged machines, universal payments and live monitoring for high-footfall sites.",
    image:
      "https://images.unsplash.com/photo-1625650484478-113df4bfc370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx2ZW5kaW5nJTIwbWFjaGluZSUyMGRhcmt8ZW58MHx8fHwxNzgyMzgyMzEwfDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function Industries() {
  const [active, setActive] = useState(0);
  const Item = industries[active];
  const Icon = Item.icon;

  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-600 mb-3">
              Solutions by Industry
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950">
              Where people work,
              <br />
              <span className="text-zinc-500">Refuel machines deliver.</span>
            </h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-5 md:gap-6">
          <div className="lg:col-span-5 flex flex-col gap-2 min-w-0">
            {industries.map((ind, i) => {
              const I = ind.icon;
              const isActive = i === active;
              return (
                <button
                  key={ind.name}
                  data-testid={`industry-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`w-full min-w-0 text-left flex items-center gap-4 p-5 rounded-2xl border transition-all ${
                    isActive
                      ? "bg-emerald-50 border-emerald-300 shadow-sm"
                      : "bg-white border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                      isActive
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "bg-zinc-50 text-zinc-700 border-zinc-200"
                    }`}
                  >
                    <I className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base md:text-lg font-semibold text-zinc-950 tracking-tight">
                      {ind.name}
                    </div>
                    <div
                      className={`text-xs ${
                        isActive ? "text-zinc-700" : "text-zinc-500"
                      } truncate`}
                    >
                      {ind.short}
                    </div>
                  </div>
                  <div
                    className={`text-xs font-bold ${
                      isActive ? "text-emerald-600" : "text-zinc-400"
                    }`}
                  >
                    0{i + 1}
                  </div>
                </button>
              );
            })}
          </div>

          <motion.div
            key={Item.name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-zinc-200 bg-white min-h-[420px]"
            data-testid="industry-active-preview"
          >
            <img
              src={Item.image}
              alt={Item.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mb-5">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-zinc-950 tracking-tight">
                {Item.name}
              </h3>
              <p className="mt-3 text-zinc-700 max-w-xl">{Item.desc}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
